import React from 'react';
import { Mail, Building, X } from 'lucide-react';
import { useUserSettings } from '../../hooks/useUserSettings';
import { useAnonymization } from '../../utils/anonymize';
import { useCompany } from '../../hooks/useCompany';
import { Link } from 'react-router-dom';
import type { Contact } from '../../types/contact';

interface ContactCardProps {
  contact: Contact;
  onRemove?: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  currentStepId?: string | null;
}

export default function ContactCard({ 
  contact, 
  onRemove,
  draggable = false,
  onDragStart,
  onDragEnd,
  isDragging,
  currentStepId
}: ContactCardProps) {
  const { settings } = useUserSettings();
  const shouldAnonymize = settings?.anonymizing_mode || false;
  
  // Move all hooks to the top level
  const firstName = contact ? useAnonymization(contact.first_name, 'name', shouldAnonymize) : '';
  const lastName = contact ? useAnonymization(contact.last_name, 'name', shouldAnonymize) : '';
  const { company } = useCompany(contact?.company_id ?? ''); // Use empty string as fallback
  const companyName = company ? useAnonymization(company.name, 'company', shouldAnonymize) : null;
  const email = contact ? (contact.work_email || contact.personal_email) : null;
  const emailAnonymized = email ? useAnonymization(email, 'email', shouldAnonymize) : null;



  if (!contact) {
    return (
      <div className="p-3 text-gray-400">
        Contact not found
      </div>
    );
  }

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      contactId: contact.id,
      currentStepId: currentStepId
    }));
    
    // Create a custom drag image
    const dragPreview = document.createElement('div');
    dragPreview.className = 'bg-dark-800 rounded-lg border border-primary-500 shadow-lg p-4 fixed -left-[9999px]';
    dragPreview.innerHTML = `
      <div class="text-lg font-semibold text-white">
        ${contact.first_name} ${contact.last_name}
      </div>
      ${contact.work_email ? `<div class="text-sm text-gray-400">${contact.work_email}</div>` : ''}
    `;
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 0, 0);
    
    // Remove the preview element after a short delay
    setTimeout(() => document.body.removeChild(dragPreview), 0);

    onDragStart?.(e);
  };

  return (
    <div 
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      className={`block bg-dark-800 rounded-lg border ${
        isDragging 
          ? 'border-primary-500 opacity-50 cursor-grabbing' 
          : 'border-dark-700 hover:border-primary-500 cursor-grab'
      } shadow-lg p-4 transition-all duration-200 space-y-3`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center space-x-3">
          <Link 
            to={`/contacts/${contact.id}`} 
            className="text-white font-medium truncate hover:text-primary-400 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {firstName} {lastName}
          </Link>
          {companyName && (
            <div className="flex items-center text-sm text-gray-400">
              <Building className="w-4 h-4 mr-1" />
              {companyName}
            </div>
          )}
        </div>
        {emailAnonymized && (
          <div className="flex items-center text-sm text-gray-400 mt-1">
            <Mail className="w-4 h-4 mr-1" />
            <div className="text-xs text-gray-400 truncate w-full overflow-hidden">
              {emailAnonymized}
            </div>
          </div>
        )}
        {contact.job_title && (
          <p className="text-sm text-gray-400 mt-1">
            {contact.job_title}
          </p>
        )}
      </div>
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}